<script setup lang="ts">
import { cva } from 'class-variance-authority';
import type { ButtonIntent, ButtonSize, ButtonTag } from 'types/base';

const BaseLink = resolveComponent('BaseLink');

type Props = {
  to?: string;
  target?: string;
  modifiers?: string[];
  intent?: ButtonIntent;
  size?: ButtonSize;
  arrowIcon?: boolean;
  title?: string;
  iconStrokeColor?: string;
  iconFillColor?: string;
  htmlTag?: ButtonTag;
  type?: 'submit' | 'button';
  disabled?: boolean;
};

const props = withDefaults(defineProps<Props>(), {
  to: undefined,
  target: undefined,
  modifiers: undefined,
  arrowIcon: true,
  size: 'large',
  intent: 'primary',
  title: undefined,
  iconStrokeColor: 'var(--color-white)',
  iconFillColor: 'transparent',
  htmlTag: 'button',
  type: 'button'
});

const baseButton = cva(
  [
    'flex',
    'justify-center',
    'items-center',
    'text-lg',
    'font-bold',
    'select-none',
    'border',
    'duration-200',
    'transition',
    'relative',
    'overflow-hidden',
    'rounded-button'
  ],
  {
    variants: {
      intent: {
        primary: ['text-white', 'border-primary', 'bg-primary', 'hover:brightness-90', 'active:brightness-75'],
        secondary: ['text-white', 'border-secondary', 'bg-secondary', 'hover:brightness-150', 'active:brightness-110'],
        ghost: ['hover:opacity-50', 'active:opacity-100'],
        quantitySelectorIncrease: [
          'border-2',
          'rounded-s-none',
          'border-gray-90',
          'hover:opacity-50',
          'active:opacity-100'
        ],
        quantitySelectorDecrease: [
          'border-2',
          'rounded-e-none',
          'border-gray-90',
          'hover:opacity-50',
          'active:opacity-100'
        ]
      },
      size: {
        extraSmall: ['py-2 px-3'],
        small: ['py-3 px-4'],
        medium: ['py-3 px-5'],
        large: ['py-4 px-8'],
        quantitySelector: ['p-3']
      },
      disabled: {
        false: null,
        true: ['opacity-65', 'pointer-events-none']
      }
    }
  }
);

const mappedButtonClasses = computed(() =>
  baseButton({
    intent: props.intent,
    size: props.size,
    disabled: props.disabled
  })
);

function mappedAttrs() {
  if (props.to) {
    return {
      to: props.to,
      target: props.target
    };
  }

  if (props.htmlTag === 'button') {
    return {
      type: props.type
    };
  }

  return {};
}
</script>

<template>
  <component
    :is="props.to ? BaseLink : props.htmlTag"
    :class="mappedButtonClasses"
    v-bind="mappedAttrs()"
  >
    <slot>{{ title }}</slot>
    <BaseIcon
      v-if="arrowIcon"
      class="ml-2 transition-transform duration-200 group-hover/item:translate-x-2"
      mode="svg"
      name="chevron-right"
      prefix="arrows"
      :stroke-color="iconStrokeColor"
      :fill-color="iconFillColor"
    />
  </component>
</template>
